import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plane, Search, Loader2 } from "lucide-react";
import { api, formatPrice, formatApiError, resolveImg } from "@/lib/api";
import OrderTimeline from "@/components/OrderTimeline";

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [num, setNum] = useState(params.get("o") || "");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const track = async (value) => {
    const q = (value ?? num).trim();
    if (!q) return;
    setBusy(true); setErr(""); setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${q}`);
      setOrder(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Order not found");
    }
    setBusy(false);
  };

  useEffect(() => { if (params.get("o")) track(params.get("o")); /* eslint-disable-next-line */ }, []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <div className="text-center">
        <Plane size={28} className="mx-auto text-amber-600" />
        <h1 className="mt-3 font-serif text-4xl text-stone-900">Track Your Order</h1>
        <p className="mt-2 text-stone-500">Enter your order number to follow its journey.</p>
      </div>

      <div className="mx-auto mt-6 flex max-w-md gap-2">
        <input
          data-testid="track-input"
          value={num}
          onChange={(e) => setNum(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && track()}
          placeholder="e.g. PRY-XXXXXXXX"
          className="flex-1 rounded-full border border-[#E7DFD3] bg-white px-4 py-3 outline-none focus:border-amber-500"
        />
        <button data-testid="track-button" onClick={() => track()} className="flex items-center gap-2 rounded-full gold-gradient px-5 font-semibold text-stone-900">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
        </button>
      </div>

      {err && <p className="mt-6 text-center text-red-600" data-testid="track-error">{err}</p>}

      {order && (
        <div className="mt-10 rounded-2xl border border-[#E7DFD3] bg-white p-6" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-mono text-lg text-stone-900">{order.order_number}</p>
            <span className="rounded-full gold-gradient px-3 py-1 text-xs font-semibold uppercase tracking-wide text-stone-900">{order.status}</span>
          </div>
          <div className="mt-10 px-2 pb-2">
            <OrderTimeline statuses={order.all_statuses} current={order.status} timeline={order.timeline} />
          </div>
          <div className="mt-8 space-y-3 border-t border-[#EFE8DC] pt-5">
            {order.items.map((i, x) => (
              <div key={x} className="flex items-center gap-3">
                <img src={resolveImg(i.image_url)} alt={i.name} className="h-12 w-12 rounded-lg object-cover" />
                <div className="flex-1"><p className="text-sm text-stone-800">{i.name}</p><p className="text-xs text-stone-500">Qty {i.qty}</p></div>
                <span className="text-sm font-medium">{formatPrice(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-between border-t border-[#EFE8DC] pt-4 font-serif text-lg">
            <span>Total</span><span>{formatPrice(order.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
